import React from 'react';
import ReactDOM from 'react-dom/client';
import {
  createBrowserRouter,
  createRoutesFromChildren,
  matchRoutes,
  RouteObject,
  RouterProvider,
  useLocation,
  useNavigationType,
} from 'react-router-dom';
import { AuthContext, ThemeContext } from 'alurkerja-ui';
import moment from 'moment';
import 'moment/dist/locale/id';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { axiosInstance } from './utils/lib/axios';
import * as Sentry from '@sentry/react';
import { routes } from './routes/root';
import { theme } from './utils/configs/theme';
import { env } from './utils/constants/env';
import { KeycloakInitializer } from './components/shared/KeycloakInitializer';



//  CSS
import './assets/index.css';
import 'alurkerja-ui/dist/style.css';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';

// moment
moment.locale('id');

// react-query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

// route
const createRouter = (routes: RouteObject[]) => createBrowserRouter(routes);
const router = createRouter(routes);

// sentry
Sentry.init({
  dsn: env.sentryDsn,
  environment: env.environment,
  integrations: [
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration(),
  ],
  tracesSampleRate: 1.0,
  // Capture Replay for 10% of all sessions,
  // plus for 100% of sessions with an error
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

// initial render
const root = document.getElementById('root');
if (!root) {
  throw new Error('Root element not found');
}

ReactDOM.createRoot(root).render(
  <QueryClientProvider client={queryClient}>
    <AuthContext.Provider value={axiosInstance}>
      <ThemeContext.Provider value={theme}>
        <KeycloakInitializer>
          <RouterProvider router={router} />
        </KeycloakInitializer>
      </ThemeContext.Provider>
      <ReactQueryDevtools initialIsOpen={env.queryDebug} />
    </AuthContext.Provider>
  </QueryClientProvider>,
);
